import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { motion } from 'framer-motion';
import { 
  FiCalendar, 
  FiUser, 
  FiClock, 
  FiEye, 
  FiShare2,
  FiArrowLeft,
  FiPlay,
  FiHeart,
  FiMessageCircle,
  FiTag
} from 'react-icons/fi';
import { FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp, FaVideo, FaNewspaper } from 'react-icons/fa';
import { getPublicBlog, likeBlog } from '../utils/api';
import { getBlogImageUrl } from '../utils/imageUtils';

// Helper to scope CSS style tags inside HTML content to avoid leakage
const scopeCSS = (htmlContent, containerClass) => {
  if (!htmlContent) return '';
  return htmlContent.replace(/<style([^>]*)>([\s\S]*?)<\/style>/gi, (match, attrs, css) => {
    const scopedCss = css.replace(/([^\r\n,{}]+)(?=\s*{[^{}]*})/g, (selector) => {
      const trimmed = selector.trim();
      if (!trimmed) return selector;
      if (trimmed.startsWith('@')) return selector;
      
      return trimmed
        .split(',')
        .map(s => {
          const part = s.trim();
          if (!part) return '';
          if (part === 'body' || part === 'html') {
            return `.${containerClass}`;
          }
          return `.${containerClass} ${part}`;
        })
        .join(', ');
    });
    return `<style${attrs}>${scopedCss}</style>`;
  });
};

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [relatedPosts, setRelatedPosts] = useState([]);

  const getBlogSchema = () => {
    if (!post) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      'headline': post.title,
      'image': getBlogImageUrl(post.image),
      'datePublished': post.created_at || new Date().toISOString(),
      'author': {
        '@type': 'Person',
        'name': post.author || 'Deeksha Rajput'
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Dfitclub',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://dfitclub.com/favicon.png'
        }
      },
      'description': post.summary || (post.content ? post.content.replace(/<[^>]*>/g, '').substring(0, 160) : '')
    };
  };

  useSEO({
    title: post ? post.title : 'Healthy Living Article',
    description: post ? (post.summary || (post.content ? post.content.replace(/<[^>]*>/g, '').substring(0, 160) : '')) : 'Read expert healthy living articles and wellness tips.',
    keywords: post ? `${post.category}, ${post.tags || ''}, nutrition article` : 'healthy living, wellness advice, nutritionist tips',
    schema: getBlogSchema()
  });

  // Fetch blog post from API
  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await getPublicBlog(id);
        setPost(data.blog);
        setLikes(data.blog.likes || 0);
        setRelatedPosts(data.related_blogs || []);
        window.scrollTo(0, 0); // Scroll to top when loading a new post
      } catch (error) {
        console.error('Error fetching blog post:', error);
        if (error.message && error.message.includes('404')) {
          setError('Post not found');
        } else {
          setError('Failed to load post');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBlogPost();
    }
  }, [id]);

  const handleLike = async () => {
    try {
      const data = await likeBlog(id);
      setLikes(data.likes);
      setLiked(!liked);
    } catch (error) {
      console.error('Error updating likes:', error);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };

  const sharePost = (platform) => {
    const url = window.location.href;
    const title = post?.title || 'Check out this post';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-4">Post Not Found</h1>
          <p className="text-neutral-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link 
            to="/blog" 
            className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/blog')}
            className="flex items-center text-neutral-600 hover:text-primary-600 transition-colors"
          >
            <FiArrowLeft className="w-5 h-5 mr-2" />
            Back to Blog
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-br from-primary-50 to-white">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${
                  post.type === 'vlog' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {post.type === 'vlog' ? <FaVideo className="w-4 h-4" /> : <FaNewspaper className="w-4 h-4" />}
                  {post.type === 'vlog' ? 'Video Blog' : 'Blog Article'}
                </span>
                <span className="px-3 py-1 bg-primary-100 text-primary-700 text-sm font-medium rounded-full">
                  {post.category}
                </span>
                {post.featured && (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                    Featured
                  </span>
                )}
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                {post.title}
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                {post.excerpt}
              </p>

              <div className="flex flex-wrap items-center gap-6 text-neutral-600">
                <div className="flex items-center gap-2">
                  <FiUser className="w-5 h-5" />
                  <span className="font-medium">{post.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiCalendar className="w-5 h-5" />
                  <span>{formatDate(post.publish_date)}</span>
                </div>
                {post.type === 'blog' && post.read_time && (
                  <div className="flex items-center gap-2">
                    <FiClock className="w-5 h-5" />
                    <span>{post.read_time}</span>
                  </div>
                )}
                {post.type === 'vlog' && post.duration && (
                  <div className="flex items-center gap-2">
                    <FiPlay className="w-5 h-5" />
                    <span>{post.duration}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <FiEye className="w-5 h-5" />
                  <span>{formatNumber(post.views || 0)} views</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Video for vlogs */}
            {post.type === 'vlog' && post.video_url && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="mb-12"
              >
                <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                  <iframe
                    src={post.video_url}
                    title={post.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              </motion.div>
            )}

            {/* Featured image for blogs */}
            {post.type === 'blog' && post.image && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="mb-12"
              >
                <img
                  src={getBlogImageUrl(post.image)}
                  alt={post.title}
                  className="w-full h-96 object-cover rounded-xl shadow-lg"
                />
              </motion.div>
            )}

            {/* Article content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-12"
            >
              {post.type === 'blog' && post.content && (
                <div 
                  dangerouslySetInnerHTML={{ __html: scopeCSS(post.content, 'blog-content-container') }}
                  className="blog-content-container prose prose-lg max-w-none text-gray-700 leading-relaxed"
                />
              )}
              
              {post.type === 'vlog' && post.video_description && (
                <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {post.video_description}
                </div>
              )}
            </motion.div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="mb-12"
              >
                <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                  <FiTag className="w-5 h-5" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-3">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-primary-50 text-primary-700 rounded-full text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 bg-neutral-50 rounded-xl mb-12"
            >
              <div className="flex items-center gap-4">
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    liked
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                >
                  <FiHeart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
                  <span>{formatNumber(likes)} Likes</span>
                </button>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-neutral-600 font-medium mr-2">Share:</span>
                <button
                  onClick={() => sharePost('facebook')}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Share on Facebook"
                >
                  <FaFacebook className="w-5 h-5" />
                </button>
                <button
                  onClick={() => sharePost('twitter')}
                  className="p-2 text-blue-400 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Share on Twitter"
                >
                  <FaTwitter className="w-5 h-5" />
                </button>
                <button
                  onClick={() => sharePost('linkedin')}
                  className="p-2 text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Share on LinkedIn"
                >
                  <FaLinkedin className="w-5 h-5" />
                </button>
                <button
                  onClick={() => sharePost('whatsapp')}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  title="Share on WhatsApp"
                >
                  <FaWhatsapp className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 bg-neutral-50">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight mb-8 text-center">Related Posts</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {relatedPosts.slice(0, 3).map((relatedPost, index) => (
                    <motion.article
                      key={relatedPost.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="relative bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-shadow group"
                    >
                      <div className="relative overflow-hidden">
                        <img
                          src={getBlogImageUrl(relatedPost.image || relatedPost.thumbnail) || '/images/blog/default.jpg'}
                          alt={relatedPost.title}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-4 left-4">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${
                            relatedPost.type === 'vlog' 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {relatedPost.type === 'vlog' ? <FaVideo className="w-3 h-3" /> : <FaNewspaper className="w-3 h-3" />}
                            {relatedPost.type === 'vlog' ? 'Video' : 'Article'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <h3 className="text-lg font-semibold text-neutral-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {relatedPost.title}
                        </h3>
                        
                        <p className="text-neutral-600 mb-4 line-clamp-2 text-sm">
                          {relatedPost.excerpt}
                        </p>
                        
                        <div className="flex items-center justify-between text-sm text-neutral-500">
                          <span>{formatDate(relatedPost.publish_date)}</span>
                          <span>{formatNumber(relatedPost.views || 0)} views</span>
                        </div>
                        
                        <Link
                          to={`/blog/${relatedPost.id}`}
                          className="absolute inset-0"
                        />
                      </div>
                    </motion.article>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;